<template>
  <PageLayout :title="$t('social.title')" :subtitle="$t('social.subtitle')">
    <template #actions>
      <button type="button" class="btn btn-primary" @click="openConnectModal">
        <i class="fas fa-plus mr-1"></i> {{ $t('social.connect_account') }}
      </button>
    </template>

    <div v-if="alertMessage" class="alert" :class="alertClass" role="alert">
      <div>{{ alertMessage }}</div>
      <div v-if="alertReason" class="small mt-2 mb-0 text-break">{{ alertReason }}</div>
    </div>

    <div v-if="connecting" class="connect-overlay" aria-live="polite" aria-busy="true">
      <div class="connect-overlay__panel">
        <div class="spinner-border text-primary mb-3" role="status" />
        <div class="font-weight-bold">{{ $t('social.connect_redirecting') }}</div>
        <div class="text-muted small mt-1">{{ connectingPlatformLabel }}</div>
      </div>
    </div>

    <ContentCard>
      <h5 class="mb-3">{{ $t('social.connected_accounts') }}</h5>

      <div v-if="accountsLoading" class="text-muted">{{ $t('common.loading_accounts') }}</div>

      <div v-else-if="accounts.length === 0" class="text-center py-5 text-muted">
        <p class="mb-3">{{ $t('social.empty') }}</p>
        <button type="button" class="btn btn-primary btn-sm" @click="openConnectModal">
          <i class="fas fa-plus mr-1"></i> {{ $t('social.connect_account') }}
        </button>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-hover accounts-table mb-0">
          <thead>
            <tr>
              <th>{{ $t('global.account') }}</th>
              <th>{{ $t('global.type') }}</th>
              <th>{{ $t('global.status') }}</th>
              <th class="text-right">{{ $t('global.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="account in accounts" :key="account.id">
              <td>
                <button type="button" class="account-cell" @click="onOpenProfile(account)">
                  <img
                    v-if="account.profile_picture_url"
                    :src="account.profile_picture_url"
                    class="account-cell__avatar"
                    alt=""
                  />
                  <span
                    v-else
                    class="account-cell__avatar account-avatar"
                    :class="`account-avatar--${account.platform || 'instagram'}`"
                  >
                    <ThreadsIcon v-if="account.platform === 'threads'" class="account-cell__threads-logo" />
                    <i v-else :class="platformIcon(account.platform)"></i>
                  </span>
                  <span class="account-cell__meta">
                    <span class="account-cell__handle">{{ accountHandle(account) }}</span>
                    <span class="account-cell__name text-muted">{{ account.display_name || '-' }}</span>
                  </span>
                </button>
              </td>
              <td>
                <span class="badge badge-light text-capitalize">{{ account.platform || 'instagram' }}</span>
              </td>
              <td>
                <span class="badge" :class="statusBadge(account.status)">{{ statusLabel(account.status) }}</span>
              </td>
              <td class="text-right text-nowrap">
                <button class="btn btn-xs btn-outline-secondary mr-1" type="button" @click="onOpenProfile(account)">
                  {{ $t('social.details') }}
                </button>
                <button class="btn btn-xs btn-outline-danger" type="button" @click="onDisconnect(account.id)">
                  {{ $t('common.disconnect') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </ContentCard>

    <modal name="connect-account-modal" height="auto" :adaptive="true" width="520" @closed="onConnectModalClosed">
      <div class="connect-modal">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 class="mb-1">{{ $t('social.connect_platform') }}</h5>
            <p class="text-muted small mb-0">{{ $t('social.connect_hint') }}</p>
          </div>
          <button type="button" class="btn btn-sm btn-link text-muted p-0" @click="closeConnectModal">×</button>
        </div>

        <div class="connect-platforms" role="radiogroup" :aria-label="$t('social.connect_platform')">
          <button
            v-for="platform in connectPlatforms"
            :key="platform.id"
            type="button"
            class="connect-platform"
            :class="{ 'connect-platform--active': selectedPlatform === platform.id }"
            role="radio"
            :aria-checked="selectedPlatform === platform.id"
            :disabled="connecting"
            @click="selectedPlatform = platform.id"
          >
            <span
              class="connect-platform__icon"
              :class="{ 'connect-platform__icon--threads': platform.id === 'threads' }"
              :style="platform.id !== 'threads' ? { background: platform.color } : null"
            >
              <ThreadsIcon v-if="platform.id === 'threads'" />
              <i v-else :class="platform.icon"></i>
            </span>
            <span class="connect-platform__name">{{ platform.name }}</span>
          </button>
        </div>

        <button
          type="button"
          class="btn btn-primary connect-action"
          :disabled="connecting || !selectedPlatform"
          @click="onConnect"
        >
          {{ $t('social.connect_continue') }}
        </button>

        <div v-if="selectedPlatform === 'instagram'" class="manual-token mt-4 pt-4 border-top">
          <h6 class="mb-2">{{ $t('social.manual_token_title') }}</h6>
          <p class="text-muted small mb-3">{{ $t('social.manual_token_hint') }}</p>
          <textarea
            v-model="manualToken"
            class="form-control font-monospace small"
            rows="3"
            :placeholder="$t('social.manual_token_placeholder')"
            :disabled="connecting"
          />
          <button
            type="button"
            class="btn btn-outline-primary mt-3"
            :disabled="connecting || !manualToken.trim()"
            @click="onConnectManualToken"
          >
            {{ connecting ? $t('social.manual_token_connecting') : $t('social.manual_token_connect') }}
          </button>
        </div>
      </div>
    </modal>

    <modal name="account-profile-modal" height="auto" :adaptive="true" width="640" :scrollable="true">
      <div class="profile-modal">
        <div class="profile-modal__head">
          <h5 class="mb-0">{{ $t('social.profile_title') }}</h5>
          <div class="profile-modal__head-actions">
            <button
              v-if="profile && !profileLoading"
              type="button"
              class="btn btn-xs btn-outline-primary"
              :disabled="profileRefreshing"
              @click="refreshProfile"
            >
              <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': profileRefreshing }"></i>
              {{ $t('social.refresh_profile') }}
            </button>
            <button type="button" class="btn btn-sm btn-link text-muted p-0 ml-2" @click="closeProfile">×</button>
          </div>
        </div>
        <div v-if="profileLoading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status" />
          <div class="text-muted small mt-2">{{ $t('social.stats_from_meta') }}</div>
        </div>
        <div v-else-if="profile">
          <div class="d-flex align-items-center mb-3">
            <img
              v-if="profile.profile_picture_url"
              :src="profile.profile_picture_url"
              class="rounded-circle mr-3"
              width="64"
              height="64"
              alt=""
            />
            <div v-else class="account-avatar account-avatar--lg mr-3" :class="`account-avatar--${profile.platform || 'instagram'}`">
              <ThreadsIcon v-if="profile.platform === 'threads'" class="profile-modal__threads-logo" />
              <i v-else :class="platformIcon(profile.platform)"></i>
            </div>
            <div class="min-w-0">
              <div class="font-weight-bold">{{ accountHandle(profile) }}</div>
              <div class="text-muted">{{ profile.display_name || profile.platform }}</div>
              <span class="badge badge-light text-capitalize mt-1">{{ profile.platform }}</span>
              <span class="badge ml-1" :class="statusBadge(profile.status)">{{ statusLabel(profile.status) }}</span>
            </div>
          </div>
          <div v-if="profile.platform === 'instagram' && !profile.username" class="alert alert-warning small mb-3">
            {{ $t('social.username_unavailable') }}
          </div>

          <h6 v-if="showProfileStats" class="profile-section-title">{{ $t('social.profile_stats') }}</h6>
          <div v-if="showProfileStats" class="profile-stats mb-3">
            <div>
              <strong>{{ formatCount(profile.followers_count) }}</strong>
              <span>{{ $t('social.followers') }}</span>
            </div>
            <div>
              <strong>{{ formatCount(profile.follows_count) }}</strong>
              <span>{{ $t('social.following') }}</span>
            </div>
            <div>
              <strong>{{ formatCount(profile.media_count) }}</strong>
              <span>{{ $t('social.posts_count') }}</span>
            </div>
          </div>

          <template v-if="profile.platform === 'instagram'">
            <h6 class="profile-section-title">{{ $t('social.insights_title') }}</h6>
            <div v-if="profile.insights" class="profile-insights mb-3">
              <div class="profile-insight">
                <span class="profile-insight__value">{{ formatCount(profile.insights.reach) }}</span>
                <span class="profile-insight__label">{{ $t('social.insight_reach') }}</span>
              </div>
              <div class="profile-insight">
                <span class="profile-insight__value">{{ formatCount(profile.insights.profile_views) }}</span>
                <span class="profile-insight__label">{{ $t('social.insight_profile_views') }}</span>
              </div>
              <div class="profile-insight">
                <span class="profile-insight__value">{{ formatCount(profile.insights.impressions) }}</span>
                <span class="profile-insight__label">{{ $t('social.insight_impressions') }}</span>
              </div>
            </div>
            <p v-else class="text-muted small mb-3">{{ $t('social.insights_unavailable') }}</p>

            <h6 v-if="profile.recent_media?.length" class="profile-section-title">{{ $t('social.recent_media_title') }}</h6>
            <div v-if="profile.recent_media?.length" class="profile-media-grid mb-3">
              <a
                v-for="item in profile.recent_media"
                :key="item.id"
                :href="item.permalink"
                target="_blank"
                rel="noopener noreferrer"
                class="profile-media-card"
              >
                <div class="profile-media-card__thumb">
                  <img v-if="mediaThumb(item)" :src="mediaThumb(item)" alt="" />
                  <span v-else class="profile-media-card__ph"><i :class="mediaTypeIcon(item.media_type)"></i></span>
                  <span v-if="isVideoMedia(item)" class="profile-media-card__badge"><i class="fas fa-play"></i></span>
                </div>
                <div class="profile-media-card__body">
                  <div class="profile-media-card__type text-capitalize">{{ item.media_type || 'media' }}</div>
                  <div class="profile-media-card__stats">
                    <span><i class="far fa-heart"></i> {{ formatCount(item.like_count) }}</span>
                    <span><i class="far fa-comment"></i> {{ formatCount(item.comments_count) }}</span>
                  </div>
                  <div class="profile-media-card__date">{{ formatMediaDate(item.timestamp) }}</div>
                </div>
                <span class="profile-media-card__link">{{ $t('social.open_on_instagram') }} <i class="fas fa-external-link-alt"></i></span>
              </a>
            </div>
          </template>

          <h6 class="profile-section-title">{{ $t('posts.details') }}</h6>
          <dl class="profile-meta mb-0">
            <div v-if="profile.account_type">
              <dt>{{ $t('social.account_type') }}</dt>
              <dd>{{ profile.account_type }}</dd>
            </div>
            <div v-if="profile.platform_user_id">
              <dt>{{ $t('social.profile_id') }}</dt>
              <dd>{{ profile.platform_user_id }}</dd>
            </div>
          </dl>
        </div>
        <p v-else-if="profileError" class="text-danger mb-0">{{ profileError }}</p>
        <div class="text-right mt-3">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeProfile">{{ $t('common.cancel') }}</button>
        </div>
      </div>
    </modal>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import i18nPage from '@/mixins/i18nPage'
import ThreadsIcon from '@/components/icons/ThreadsIcon.vue'

const CONNECT_PLATFORMS = [
  {
    id: 'instagram',
    name: 'Instagram',
    icon: 'fab fa-instagram',
    color: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: 'fab fa-facebook-f',
    color: '#1877F2',
  },
  {
    id: 'threads',
    name: 'Threads',
    icon: 'threads',
    color: '#000000',
  },
]

export default {
  components: { ThreadsIcon },
  mixins: [i18nPage],
  data() {
    return {
      selectedPlatform: 'instagram',
      manualToken: '',
      connecting: false,
      alertMessage: '',
      alertReason: '',
      alertClass: 'alert-info',
      profile: null,
      profileAccountId: null,
      profileLoading: false,
      profileRefreshing: false,
      profileError: '',
    }
  },
  computed: {
    ...mapGetters('app', ['accounts', 'accountsLoading']),
    connectPlatforms() {
      return CONNECT_PLATFORMS
    },
    connectingPlatformLabel() {
      const platform = CONNECT_PLATFORMS.find((p) => p.id === this.selectedPlatform)
      return platform ? platform.name : ''
    },
    showProfileStats() {
      const p = this.profile
      if (!p) return false
      return p.platform === 'instagram' || p.platform === 'threads'
    },
  },
  async created() {
    await this.fetchAccounts()
    this.checkQueryParams()
  },
  methods: {
    ...mapActions('app', ['fetchAccounts', 'connectPlatform', 'connectInstagramToken', 'disconnectAccount', 'fetchAccountProfile']),
    ...mapMutations('app', { patchAccount: 'PATCH_ACCOUNT' }),
    accountHandle(account) {
      if (account?.username) return `@${account.username}`
      if (account?.display_name) return account.display_name
      return account?.platform || 'Instagram'
    },
    formatCount(value) {
      const n = Number(value || 0)
      if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
      if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
      return n.toLocaleString()
    },
    formatMediaDate(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleDateString(this.intlLocale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    },
    mediaThumb(item) {
      return item.thumbnail_url || item.media_url || ''
    },
    isVideoMedia(item) {
      const t = (item.media_type || '').toUpperCase()
      return t === 'VIDEO' || t === 'REELS'
    },
    mediaTypeIcon(mediaType) {
      const t = (mediaType || '').toUpperCase()
      if (t === 'REELS' || t === 'VIDEO') return 'fas fa-film'
      if (t === 'CAROUSEL_ALBUM') return 'fas fa-images'
      return 'fas fa-image'
    },
    async loadProfileDetails(id, { silent = false } = {}) {
      if (!silent) {
        this.profileLoading = true
      } else {
        this.profileRefreshing = true
      }
      this.profileError = ''
      try {
        const data = await this.fetchAccountProfile(id)
        this.profile = data
        this.patchAccount(data)
      } catch (e) {
        this.profileError = e.response?.data?.message || this.$t('social.profile_load_failed')
      } finally {
        this.profileLoading = false
        this.profileRefreshing = false
      }
    },
    async onOpenProfile(account) {
      this.profile = account
      this.profileAccountId = account.id
      this.profileError = ''
      this.$modal.show('account-profile-modal')
      await this.loadProfileDetails(account.id)
    },
    async refreshProfile() {
      if (!this.profileAccountId) return
      await this.loadProfileDetails(this.profileAccountId, { silent: true })
    },
    closeProfile() {
      this.$modal.hide('account-profile-modal')
    },
    openConnectModal() {
      this.selectedPlatform = 'instagram'
      this.manualToken = ''
      this.$modal.show('connect-account-modal')
    },
    closeConnectModal() {
      if (!this.connecting) {
        this.$modal.hide('connect-account-modal')
      }
    },
    onConnectModalClosed() {
      if (!this.connecting) {
        this.manualToken = ''
        this.selectedPlatform = 'instagram'
      }
    },
    platformIcon(platform) {
      const map = {
        instagram: 'fab fa-instagram',
        facebook: 'fab fa-facebook-f',
        threads: null,
        linkedin: 'fab fa-linkedin-in',
        x: 'fab fa-twitter',
      }
      return map[platform] || 'fab fa-instagram'
    },
    checkQueryParams() {
      const { connected, error, reason } = this.$route.query
      const errors = {
        no_instagram_account: 'social.no_ig_account',
        no_facebook_page: 'social.no_facebook_page',
        no_threads_account: 'social.no_threads_account',
        missing_permissions: 'social.missing_permissions',
        denied: 'social.denied',
        account_limit: 'social.account_limit',
        connection_failed: 'social.connection_failed',
      }
      if (connected) {
        const name = String(connected).charAt(0).toUpperCase() + String(connected).slice(1)
        this.alertMessage = this.$t('social.connected_success_named', { platform: name })
        this.alertClass = 'alert-success'
        return
      }
      if (!errors[error]) return
      this.alertMessage = this.$t(errors[error])
      this.alertReason = Array.isArray(reason) ? reason[0] : (reason || '')
      this.alertClass = 'alert-warning'
    },
    async onConnect() {
      if (!this.selectedPlatform || this.connecting) return
      this.connecting = true
      try {
        await this.connectPlatform(this.selectedPlatform)
      } catch (e) {
        this.connecting = false
        this.$toast?.error?.(e.response?.data?.message || this.$t('social.connect_failed'))
      }
    },
    async onConnectManualToken() {
      const token = this.manualToken.trim()
      if (!token || this.connecting) return
      this.connecting = true
      this.alertMessage = ''
      this.alertReason = ''
      try {
        await this.connectInstagramToken(token)
        this.manualToken = ''
        this.closeConnectModal()
        this.alertMessage = this.$t('social.connected_success_named', { platform: 'Instagram' })
        this.alertClass = 'alert-success'
        this.$toast?.success?.(this.$t('social.manual_token_success'))
      } catch (e) {
        this.$toast?.error?.(e.response?.data?.message || this.$t('social.connect_failed'))
      } finally {
        this.connecting = false
      }
    },
    async onDisconnect(id) {
      const ok = await this.$confirm(this.$t('social.disconnect_confirm'), { danger: true })
      if (!ok) return
      try {
        await this.disconnectAccount(id)
        this.$toast?.success?.(this.$t('social.disconnect_success'))
      } catch (e) {
        this.$toast?.error?.(this.$t('social.disconnect_failed'))
      }
    },
    statusBadge(status) {
      return status === 'active' ? 'badge-success' : 'badge-secondary'
    },
    statusLabel(status) {
      return status === 'active' ? this.$t('social.status_active') : this.$t('social.status_inactive')
    },
  },
}
</script>

<style scoped>
.connect-modal {
  padding: 1.25rem 1.4rem 1.4rem;
}

.connect-platforms {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.connect-platform {
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
  border: 1px solid #e8ecf1;
  border-radius: 999px;
  background: #fff;
  color: #3e5569;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.connect-platform:hover:not(:disabled) {
  border-color: #c5d0dc;
}

.connect-platform--active {
  border-color: var(--wc-primary, #4798e8);
  background: rgba(71, 152, 232, 0.08);
  box-shadow: 0 0 0 1px rgba(71, 152, 232, 0.15);
}

.connect-platform:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.connect-platform__icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.connect-platform__icon svg {
  width: 16px;
  height: 16px;
}

.connect-platform__icon--threads {
  background: #000 !important;
  border: none;
  color: #fff;
}

.connect-platform__icon--threads .threads-icon {
  width: 18px;
  height: 18px;
  font-size: 18px;
}

.connect-platform__name {
  font-size: 0.875rem;
  font-weight: 600;
}

.connect-action {
  min-width: 220px;
  width: 100%;
}

.connect-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(2px);
}

.connect-overlay__panel {
  text-align: center;
  padding: 1.5rem 2rem;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
}

.accounts-table td {
  vertical-align: middle;
}

.account-cell {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  max-width: 100%;
}

.account-cell:hover .account-cell__handle {
  color: var(--wc-primary, #4798e8);
}

.account-cell__avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
}

.account-cell__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.account-cell__handle {
  font-weight: 600;
  line-height: 1.3;
}

.account-cell__name {
  font-size: 0.8125rem;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-avatar--lg {
  width: 64px;
  height: 64px;
  font-size: 1.5rem;
}

.profile-modal {
  padding: 1.25rem 1.4rem 1.4rem;
}

.profile-modal__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.profile-modal__head-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.profile-section-title {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6c757d;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.profile-insights {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.65rem;
}

.profile-insight {
  padding: 0.65rem 0.5rem;
  border: 1px solid #e8ecf1;
  border-radius: 10px;
  text-align: center;
  background: #fafbfc;
}

.profile-insight__value {
  display: block;
  font-weight: 700;
  font-size: 1.05rem;
  line-height: 1.2;
}

.profile-insight__label {
  display: block;
  font-size: 0.68rem;
  color: #6c757d;
  margin-top: 0.15rem;
}

.profile-media-grid {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-height: 280px;
  overflow-y: auto;
}

.profile-media-card {
  display: grid;
  grid-template-columns: 72px 1fr auto;
  gap: 0.65rem;
  align-items: center;
  padding: 0.5rem;
  border: 1px solid #e8ecf1;
  border-radius: 10px;
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.profile-media-card:hover {
  border-color: #c5d0dc;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  text-decoration: none;
  color: inherit;
}

.profile-media-card__thumb {
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f0f0;
}

.profile-media-card__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.profile-media-card__ph {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
}

.profile-media-card__badge {
  position: absolute;
  bottom: 4px;
  left: 4px;
  font-size: 0.6rem;
  padding: 2px 5px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
}

.profile-media-card__type {
  font-size: 0.75rem;
  font-weight: 600;
  color: #444;
}

.profile-media-card__stats {
  display: flex;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: #6c757d;
}

.profile-media-card__date {
  font-size: 0.7rem;
  color: #9aa5b1;
}

.profile-media-card__link {
  font-size: 0.7rem;
  color: var(--wc-primary, #4798e8);
  white-space: nowrap;
}

.profile-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  text-align: center;
}

.profile-stats span {
  display: block;
  font-size: 0.75rem;
  color: #6c757d;
}

.profile-meta dt {
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.1rem;
}

.profile-meta dd {
  margin-bottom: 0.75rem;
  word-break: break-all;
}

.account-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
}

.account-avatar--instagram {
  background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
}

.account-avatar--facebook {
  background: #1877F2;
}

.account-avatar--threads {
  background: #000;
  color: #fff;
}

.account-cell__threads-logo,
.profile-modal__threads-logo {
  width: 1.1em;
  height: 1.1em;
  font-size: 1.1em;
}

.account-avatar--linkedin {
  background: #0A66C2;
}

.account-avatar--x {
  background: #000;
}
</style>
